import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Icon } from './Icon';
import { FaTimes } from 'react-icons/fa';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  position: relative;
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @media (max-width: 768px) {
    margin: 10px;
    max-height: 85vh;
  }
`;

const ModalHeader = styled.div`
  padding: 20px 24px 0 24px;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: sticky;
  top: 0;
  background: #2a2a2a;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  color: #ffffff;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 16px;
  flex-shrink: 0;
  
  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ModalContent = styled.div`
  padding: 20px 24px 24px 24px;
  
  /* Base typography styles */
  h1, h2, h3, h4, h5, h6 {
    color: #ffffff;
    margin-top: 24px;
    margin-bottom: 12px;
    font-weight: 600;
    line-height: 1.3;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  h1 { font-size: 28px; }
  h2 { font-size: 24px; }
  h3 { font-size: 20px; }
  h4 { font-size: 18px; }
  h5 { font-size: 16px; }
  h6 { font-size: 14px; }
  
  p {
    margin: 0 0 16px 0;
    line-height: 1.6;
    color: #cccccc;
  }
  
  ul, ol {
    margin: 0 0 16px 0;
    padding-left: 24px;
    
    li {
      margin-bottom: 8px;
      line-height: 1.6;
      color: #cccccc;
    }
  }
  
  blockquote {
    margin: 16px 0;
    padding: 12px 16px;
    border-left: 4px solid #007bff;
    background-color: #1a1a1a;
    font-style: italic;
    
    p {
      margin: 0;
      color: #cccccc;
    }
  }
  
  code {
    background-color: #1a1a1a;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
    color: #ff79c6;
  }
  
  pre {
    background-color: #1a1a1a;
    border: 1px solid #444;
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 16px 0;
    
    code {
      background: none;
      padding: 0;
      color: #cccccc;
    }
  }
  
  /* Image styles */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 16px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  /* Video container for responsive videos */
  .video-container {
    position: relative;
    width: 100%;
    margin: 16px 0;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  /* YouTube embed responsive */
  iframe {
    max-width: 100%;
    border-radius: 6px;
    margin: 16px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 14px;
    border: 1px solid #444;
    border-radius: 6px;
    overflow: hidden;
    
    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #444;
      color: #cccccc;
    }
    
    th {
      background-color: #1a1a1a;
      font-weight: 600;
      color: #ffffff;
    }
    
    tr:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
  
  /* Links */
  a {
    color: #007bff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  /* Badges and labels */
  .badge {
    display: inline-block;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: 12px;
    background-color: #6c757d;
    color: white;
    margin: 0 4px;
    
    &.primary { background-color: #007bff; }
    &.success { background-color: #28a745; }
    &.warning { background-color: #ffc107; color: #212529; }
    &.danger { background-color: #dc3545; }
    &.info { background-color: #17a2b8; }
  }
  
  /* Alert boxes */
  .alert {
    padding: 12px 16px;
    margin: 16px 0;
    border: 1px solid;
    border-radius: 6px;
    
    &.info {
      color: #7dd3fc;
      background-color: #0c1629;
      border-color: #1e3a8a;
    }
    
    &.success {
      color: #86efac;
      background-color: #0a1f0a;
      border-color: #166534;
    }
    
    &.warning {
      color: #fbbf24;
      background-color: #1f1611;
      border-color: #92400e;
    }
    
    &.danger {
      color: #f87171;
      background-color: #1f1012;
      border-color: #991b1b;
    }
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    padding: 16px 20px 20px 20px;
    
    h1 { font-size: 24px; }
    h2 { font-size: 20px; }
    h3 { font-size: 18px; }
    
    table {
      font-size: 12px;
      
      th, td {
        padding: 8px;
      }
    }
  }
`;

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}

export const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  onClose,
  title,
  content
}) => {
  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking outside
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={onClose} title="Close help">
            <Icon icon={FaTimes} />
          </CloseButton>
        </ModalHeader>
        <ModalContent dangerouslySetInnerHTML={{ __html: content }} />
      </ModalContainer>
    </ModalOverlay>
  );
};